
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  private static recentDomErrors: number[] = [];

  /**
   * Erros de DOM "de limpeza" (portais/animações sendo removidos duas vezes,
   * extensões de tradução etc.). Safari usa "The object can not be found here",
   * Chrome usa removeChild/insertBefore. São inofensivos após o próximo render.
   */
  private static isRecoverableDomError(error: Error): boolean {
    const msg = error?.message || '';
    const name = (error as any)?.name || '';
    const code = (error as any)?.code;

    if (name === 'NotFoundError' || name === 'HierarchyRequestError') return true;
    if (typeof DOMException !== 'undefined' && error instanceof DOMException && (code === 8 || code === 3)) {
      return true;
    }

    return (
      msg.includes('The object can not be found here') ||
      msg.includes('The object can not be found here.') ||
      msg.includes('not a child of this node') ||
      msg.includes('removeChild') ||
      msg.includes('insertBefore')
    );
  }

  /** Retorna true quando o mesmo tipo de erro se repete demais (3 em 10s). */
  private static shouldEscalate(): boolean {
    const now = Date.now();
    ErrorBoundary.recentDomErrors = ErrorBoundary.recentDomErrors.filter((t) => now - t < 10_000);
    ErrorBoundary.recentDomErrors.push(now);
    return ErrorBoundary.recentDomErrors.length >= 3;
  }

  public static getDerivedStateFromError(error: Error): State {
    if (ErrorBoundary.isRecoverableDomError(error)) {
      if (!ErrorBoundary.shouldEscalate()) {
        console.warn('ErrorBoundary: ignoring recoverable DOM error:', error.message);
        return { hasError: false };
      }
      console.error('ErrorBoundary: recoverable DOM error repetido demais, exibindo aviso:', error.message);
      return { hasError: true, error };
    }
    console.error('ErrorBoundary caught error:', error);
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (ErrorBoundary.isRecoverableDomError(error) && !this.state.hasError) {
      return;
    }

    
    // Log detalhado para auditoria técnica
    const diagnosticData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`
    };
    
    console.error('Error caught by boundary:', diagnosticData);
    this.setState({ errorInfo });
  }

  private handleReset = () => {
    console.log('Resetting error boundary');
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    // Force a page reload to clear any corrupted state
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-lg">Algo deu errado</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Ocorreu um erro inesperado. Por favor, recarregue a página.
              </p>
              {this.state.error && (
                <details className="text-xs text-left bg-gray-50 p-3 rounded border">
                  <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                    Detalhes do erro
                  </summary>
                  <pre className="whitespace-pre-wrap text-red-600 mb-2">
                    {this.state.error.message}
                  </pre>
                  {this.state.errorInfo && (
                    <pre className="whitespace-pre-wrap text-gray-600 text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </details>
              )}
              <Button onClick={this.handleReset} className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                Recarregar Página
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
